import Logger from 'utils/logger';

export interface JwtPayload {
  sub: string;
  email?: string;
  identities?: Array<{
    userId: string;
    providerName: string;
    providerType: string;
    issuer: string | null;
    primary: string;
    dateCreated: string;
  }>;
  iss: string;
  aud: string;
  exp: number;
  iat: number;
  [key: string]: unknown;
}

export function decodeJwt(token: string): JwtPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      Logger.warn(
        `[Auth][JWT] Token is not a valid JWT (${
          parts.length
        } parts, expected 3). Preview: ${token.substring(0, 20)}...`,
      );
      return null;
    }

    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded) as JwtPayload;
  } catch (e) {
    Logger.warn(
      `[Auth][JWT] Failed to decode JWT. Preview: ${token.substring(0, 20)}...`,
      (e as Error).message,
    );
    return null;
  }
}

export function isTokenExpired(token: string): boolean {
  const payload = decodeJwt(token);
  if (!payload) {
    Logger.warn(
      '[Auth][JWT] isTokenExpired: token not decodable, treating as expired',
    );
    return true;
  }

  const currentTime = Math.floor(Date.now() / 1000);
  const expired = payload.exp < currentTime;
  const ttlSeconds = payload.exp - currentTime;

  if (expired) {
    Logger.warn(
      `[Auth][JWT] Token expired ${Math.abs(ttlSeconds)}s ago. sub=${
        payload.sub
      }`,
    );
  } else {
    Logger.log(
      `[Auth][JWT] Token valid, expires in ${ttlSeconds}s. sub=${payload.sub}`,
    );
  }

  return expired;
}

export function getTokenExpirationTime(token: string): number | null {
  const payload = decodeJwt(token);
  return payload?.exp ?? null;
}
